import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { AvatarCircles } from '@rocco/components/avatar-circles';
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from '@rocco/components/hover-card';
import type { ReactionVoter } from '~/modules/build-hub/data-mappers/map-inspiration-reactions';
import { VoterPreview } from './voter-preview';

interface VotingControlProps {
    /** Whether the user has voted up */
    hasVotedUp?: boolean;
    /** Whether the user has voted down */
    hasVotedDown?: boolean;
    /** Whether the user can vote */
    canVote?: boolean;
    /** List of collaborators who have upvoted */
    upVoteCollaborators?: ReactionVoter[];
    /** List of collaborators who have downvoted */
    downVoteCollaborators?: ReactionVoter[];
    /** Callback when up vote is clicked */
    onVoteUp?: () => void;
    /** Callback when down vote is clicked */
    onVoteDown?: () => void;
    /** Additional CSS classes */
    className?: string;
    /** Whether the user is voting */
    isLoading?: boolean;
}

/**
 * Voting control component with up/down buttons, count, and collaborator avatars
 *
 * @example
 * ```tsx
 * <VotingControl
 *   count={42}
 *   hasVotedUp={true}
 *   hasVotedDown={false}
 *   upVoteCollaborators={[
 *     { imageUrl: "/avatar1.jpg", name: "John Doe" },
 *     { imageUrl: "/avatar2.jpg", name: "Jane Smith" }
 *   ]}
 *   onVoteUp={() => handleVoteUp()}
 *   onVoteDown={() => handleVoteDown()}
 * />
 * ```
 */
export const VotingControl = ({
    isLoading = false,
    hasVotedUp = false,
    hasVotedDown = false,
    canVote = true,
    upVoteCollaborators = [],
    downVoteCollaborators = [],
    onVoteUp,
    onVoteDown,
    className,
}: VotingControlProps) => {
    const handleVoteUp = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (canVote && onVoteUp) {
            onVoteUp();
        }
    };

    const handleVoteDown = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (canVote && onVoteDown) {
            onVoteDown();
        }
    };

    return (
        <div
            className={cn(
                'inline-flex items-center rounded-full bg-gray-100',
                className
            )}
        >
            {/* Up vote button and collaborators */}
            <div className="flex items-center">
                {upVoteCollaborators.length > 0 ? (
                    <HoverCard openDelay={400} closeDelay={100}>
                        <HoverCardTrigger asChild>
                            <button
                                onClick={handleVoteUp}
                                disabled={!canVote || isLoading}
                                className={cn(
                                    'flex items-center justify-center gap-1 transition-colors',
                                    'hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50',
                                    hasVotedUp && 'text-[#7BD971]',
                                    'rounded-l-full pb-2 pl-3 pr-2 pt-2'
                                )}
                                aria-label="Vote up"
                            >
                                <div
                                    className={cn(
                                        'flex size-6 items-center justify-center'
                                    )}
                                >
                                    <ThumbsUp
                                        size={16}
                                        className={cn(
                                            'size-4',
                                            hasVotedUp
                                                ? 'fill-current'
                                                : 'stroke-current transition-colors'
                                        )}
                                    />
                                </div>
                                {/* Display upvote collaborators */}
                                <AvatarCircles
                                    avatarUrls={upVoteCollaborators.map(
                                        (voter) => ({
                                            imageUrl: voter.profileImages ?? '',
                                            name:
                                                voter.name ?? voter.email ?? '',
                                        })
                                    )}
                                    size="square24"
                                    border="light"
                                />
                            </button>
                        </HoverCardTrigger>
                        <HoverCardContent
                            side="top"
                            align="start"
                            sideOffset={8}
                            className="z-[100] w-auto border-none bg-transparent p-0"
                        >
                            <VoterPreview
                                voters={upVoteCollaborators}
                                voteType="upvote"
                            />
                        </HoverCardContent>
                    </HoverCard>
                ) : (
                    <button
                        onClick={handleVoteUp}
                        disabled={!canVote || isLoading}
                        className={cn(
                            'flex items-center justify-center gap-1 transition-colors',
                            'hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50',
                            hasVotedUp && 'text-[#7BD971]',
                            'rounded-l-full pb-2 pl-3 pr-2 pt-2'
                        )}
                        aria-label="Vote up"
                    >
                        <div
                            className={cn(
                                'flex size-6 items-center justify-center'
                            )}
                        >
                            <ThumbsUp
                                size={16}
                                className={cn(
                                    'size-4',
                                    hasVotedUp
                                        ? 'fill-current'
                                        : 'stroke-current transition-colors'
                                )}
                            />
                        </div>
                        {/* Display upvote collaborators */}
                        <span className="text-center text-sm text-gray-900">
                            {0}
                        </span>
                    </button>
                )}
            </div>

            {/* Separator */}
            <div className="h-4 w-px bg-gray-300" />

            {/* Down vote button */}
            {downVoteCollaborators.length > 0 ? (
                <HoverCard openDelay={400} closeDelay={100}>
                    <HoverCardTrigger asChild>
                        <button
                            onClick={handleVoteDown}
                            disabled={!canVote || isLoading}
                            className={cn(
                                'flex items-center justify-center transition-colors',
                                'hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50',
                                hasVotedDown && 'text-red-600',
                                'rounded-r-full pb-2 pl-2 pr-3 pt-2'
                            )}
                            aria-label="Vote down"
                        >
                            <div className="flex size-6 items-center justify-center">
                                <ThumbsDown
                                    size={16}
                                    className={cn(
                                        'size-4',
                                        hasVotedDown
                                            ? 'fill-current'
                                            : 'stroke-current transition-colors'
                                    )}
                                />
                            </div>
                            {/* Display downvote collaborators */}
                            <AvatarCircles
                                avatarUrls={downVoteCollaborators.map(
                                    (voter) => ({
                                        imageUrl: voter.profileImages ?? '',
                                        name: voter.name ?? voter.email ?? '',
                                    })
                                )}
                                size="square24"
                                border="light"
                            />
                        </button>
                    </HoverCardTrigger>
                    <HoverCardContent
                        side="top"
                        align="start"
                        sideOffset={8}
                        className="z-[100] w-auto border-none bg-transparent p-0"
                    >
                        <VoterPreview
                            voters={downVoteCollaborators}
                            voteType="downvote"
                        />
                    </HoverCardContent>
                </HoverCard>
            ) : (
                <button
                    onClick={handleVoteDown}
                    disabled={!canVote || isLoading}
                    className={cn(
                        'flex items-center justify-center transition-colors',
                        'hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50',
                        hasVotedDown && 'text-red-600',
                        'rounded-r-full pb-2 pl-2 pr-3 pt-2'
                    )}
                    aria-label="Vote down"
                >
                    <div className="flex size-6 items-center justify-center">
                        <ThumbsDown
                            size={16}
                            className={cn(
                                'size-4',
                                hasVotedDown
                                    ? 'fill-current'
                                    : 'stroke-current transition-colors'
                            )}
                        />
                    </div>
                    {/* Display downvote collaborators */}
                    <span className="text-center text-sm text-gray-900">
                        {0}
                    </span>
                </button>
            )}
        </div>
    );
};
